// Client-side wrapper around the /api/vision serverless function.
//
// The client never talks to the Anthropic Application Programming Interface (API) directly.
// The API key lives only on the server. This module:
//   1. Reads the user's cloud opt-in preference from localStorage.
//   2. If opted in: posts the photo to /api/vision and returns the parsed result.
//   3. If not opted in: returns a manual-mode result with no questions, signalling
//      the User Interface (UI) to fall back to the "tap a slot, then dictate" flow.

import type { WorksheetQuestion } from './vision-types';

export type { WorksheetQuestion, QuestionBox, VisionResponse } from './vision-types';

// AI vision is ON by default. Users opt OUT via Settings. The storage key records
// "is the user opted out", so an unset key means default (enabled). All safeguarding
// rules (no body images, no faces, document-only) still apply identically in cloud and
// manual modes.
const STORAGE_KEY_CLOUD_DISABLED = 'mdt_cloud_vision_disabled';

export interface VisionAnalysis {
  source: 'cloud' | 'manual';
  documentDetected: boolean;
  orientation: 'portrait' | 'landscape';
  questions: WorksheetQuestion[];
  /** Full readable worksheet text, used by /api/answer-cleanup. */
  documentText?: string;
  /** Subject classification (canonical lower-case form). */
  subject?: string;
  /** Display label for the subject. */
  subjectLabel?: string;
  /** Year level (Year 4, GCSE, etc.) if obvious. */
  yearLevel?: string;
  /** Short auto-generated title for the worksheet. */
  suggestedTitle?: string;
}

export function isCloudVisionEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_CLOUD_DISABLED) !== 'true';
  } catch {
    return true;
  }
}

export function setCloudVisionEnabled(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY_CLOUD_DISABLED, String(!enabled));
  } catch {
    // Storage unavailable - silently ignore. The preference falls back to the default.
  }
}

// Deprecated names kept temporarily so any imports we missed still compile.
// Remove once everything reads the new names.
export const hasCloudOptIn = isCloudVisionEnabled;
export const setCloudOptIn = setCloudVisionEnabled;

/**
 * Analyse a worksheet photo.
 *
 * @param dataUrl - the image as a data URL (e.g. "data:image/jpeg;base64,...")
 * @returns the parsed vision result, or a manual-mode fallback
 */
export async function analyseWorksheet(dataUrl: string): Promise<VisionAnalysis> {
  if (!isCloudVisionEnabled()) {
    return { source: 'manual', documentDetected: true, orientation: 'portrait', questions: [] };
  }

  const { base64, mimeType } = splitDataUrl(dataUrl);
  if (!base64) {
    return { source: 'manual', documentDetected: true, orientation: 'portrait', questions: [] };
  }

  const response = await fetch('/api/vision', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageBase64: base64, mimeType }),
  });

  if (!response.ok) {
    // Bubble up so the UI can render a friendly error and offer manual mode.
    const detail = await safeReadJson(response);
    const message = (detail as { error?: string })?.error ?? `HTTP ${response.status}`;
    throw new VisionError(message);
  }

  const json = (await response.json()) as Awaited<ReturnType<typeof response.json>> & {
    documentDetected?: boolean;
    orientation?: 'portrait' | 'landscape';
    questions?: WorksheetQuestion[];
  };

  const typed = json as {
    documentDetected?: boolean;
    orientation?: 'portrait' | 'landscape';
    questions?: WorksheetQuestion[];
    documentText?: string;
    subject?: string;
    subjectLabel?: string;
    yearLevel?: string;
    suggestedTitle?: string;
  };

  return {
    source: 'cloud',
    documentDetected: Boolean(typed.documentDetected),
    orientation: typed.orientation === 'landscape' ? 'landscape' : 'portrait',
    questions: Array.isArray(typed.questions) ? typed.questions : [],
    documentText: typeof typed.documentText === 'string' ? typed.documentText : undefined,
    subject: typeof typed.subject === 'string' ? typed.subject : undefined,
    subjectLabel: typeof typed.subjectLabel === 'string' ? typed.subjectLabel : undefined,
    yearLevel: typeof typed.yearLevel === 'string' ? typed.yearLevel : undefined,
    suggestedTitle: typeof typed.suggestedTitle === 'string' ? typed.suggestedTitle : undefined,
  };
}

export class VisionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VisionError';
  }
}

function splitDataUrl(dataUrl: string): { base64: string; mimeType: 'image/jpeg' | 'image/png' | 'image/webp' } {
  const match = /^data:(image\/(jpeg|png|webp));base64,(.+)$/.exec(dataUrl);
  if (!match) return { base64: '', mimeType: 'image/jpeg' };
  return {
    base64: match[3],
    mimeType: match[1] as 'image/jpeg' | 'image/png' | 'image/webp',
  };
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}
